/**
 * Main JavaScript entry point.
 * This file is responsible for initializing all components and handling global events.
 * It sets up event listeners for DOMContentLoaded, resize, and orientationchange events.
 * It also manages the rendering of React components for dynamic search functionality.
 *
 * If you need to add custom Javascript, please check if it's specifically for a template.
 * If so, add it to the `client-<template-name>.js` file.
 * That way, the code is only loaded when necessary.
 *
 * @package nl-design-system-theme
 */
import React from "react";
import { createRoot } from "react-dom/client";
import CalamityBanner from "./components/CalamityBanner";
import ContentGuruChat from "./components/ContentGuruChat";
import debounce from "./components/debounce";
import DenhaagBreadcrumbs from "./components/DenhaagBreadcrumbs";
import DenhaagButtonAnimation from "./components/DenhaagButtonAnimation";
import DenhaagCardsAnimation from "./components/DenhaagCardsAnimation";
import DetectBrowser from "./components/detectBrowser";
import DynamicSearch from "./components/DynamicSearch";
import FloatingContact from "./components/FloatingContact";
import focusElement from "./components/focus-elements";
import FooterAccordionFocus from "./components/FooterAccordionFocus";
import FooterNavToggle from "./components/FooterNavToggle";
import HeaderPanels from "./components/HeaderPanels";
import MobileMenu from "./components/MobileMenu";
import { extendOpenstadShortcode } from "./components/OpenstadShortcode";

const detector = new DetectBrowser();
const headerPanels = new HeaderPanels();
const mobileMenu = new MobileMenu();

let windowWidth = window.innerWidth;

/**
 * Send the height to parent window.
 */
const sendHeight = () => window.parent.postMessage( ['height', Math.ceil( document.documentElement.scrollHeight )], '*' );

// Only append when the DOM is ready, but before full load. So u don`t need CSS selectors.
document.onreadystatechange = () => document.readyState === 'interactive' && detector.setupEventListeners();

window.addEventListener( "DOMContentLoaded", () => {
  headerPanels.initialize();
  headerPanels.setupEventListeners();
  mobileMenu.initialize();
  mobileMenu.setupEventListeners();

  extendOpenstadShortcode(".openstad .documentMap--container");

  new FooterAccordionFocus();
  FooterNavToggle( ".denhaag-footer--mega .denhaag-footer__a11y-button" );

  new CalamityBanner();
  new DenhaagBreadcrumbs();
  new DenhaagButtonAnimation();
  new FloatingContact();
  new DenhaagCardsAnimation();

  [...document.getElementsByClassName( "denhaag-search-form--dynamic" )]?.forEach( (element, searchBoxIndex) => createRoot( element )?.render(
    <DynamicSearch
      id={element.getAttribute( "id" )}
      endpoint='rest_api_url'
      searchBoxIndex={`dynamic-${searchBoxIndex}`}
      resultsHeight={element.dataset.resultsHeight}
    />
  ) );

  // Initialize ContentGuruChat.
  new ContentGuruChat();

  focusElement();
} );

// Handle resize events.
window.addEventListener( "resize", debounce( () => {
  if (window.innerWidth !== windowWidth) {
    // Dispatch a standard resize event to trigger other listeners.
    window.dispatchEvent( new Event( "dh:resize" ) );
    windowWidth = window.innerWidth;
  }
} ) );

// Dispatch a standard resize event to trigger other listeners.
window.addEventListener( "orientationchange", debounce( () => window.dispatchEvent( new Event( "dh:resize" ) ) ) );

// Handle custom dh:resize events, will be triggered on resize and orientationchange.w
window.addEventListener( "dh:resize", () => {
  FooterNavToggle( '.denhaag-footer--mega .denhaag-footer__a11y-button' );

  detector.resize();
  headerPanels.resize();
  mobileMenu.resize();

  sendHeight();
} );

if (window.self !== window.top) {
  window.addEventListener( 'load', debounce( () => sendHeight()
    , 50 ), {once: true} );
}

/**
 * LiveCom chat will fire events.
 *
 * @param {string} available currently only receiving `on` and `off` with a `busy` (very rare).
 * @param {number} status currently only receiving `-1` and `0`.
 */
window.changeChatStatus = function changeStatus(available, status) {
  [...document.getElementsByClassName( "denhaag-chat-status" )]?.forEach(
    (trigger) => switchChatStatus( trigger, available, "denhaag-chat-status" )
  );
  switchChatStatus(
    document.getElementById( "livecom-trigger" ),
    available,
    "denhaag-floating-contact__item"
  );
};

function switchChatStatus(el, status, className) {
  switch (status) {
    case "on":
      el.classList.add( `${className}--online` );
      if (el.classList.contains( `${className}--offline` )) {
        el.classList.remove( `${className}--offline` );
      }
      if (el.classList.contains( `${className}--disabled` )) {
        el.classList.remove( `${className}--disabled` );
      }
      break;

    case "off":
      el.classList.add( `${className}--offline` );
      if (el.classList.contains( `${className}--online` )) {
        el.classList.remove( `${className}--online` );
      }
      if (el.classList.contains( `${className}--disabled` )) {
        el.classList.remove( `${className}--disabled` );
      }
      break;
    default:
      el.classList.add( `${className}--disabled` );
      if (el.classList.contains( `${className}--online` )) {
        el.classList.remove( `${className}--online` );
      }
      if (el.classList.contains( `${className}--offline` )) {
        el.classList.remove( `${className}--offline` );
      }
  }
}

document.addEventListener( 'gform_post_render', function () {
    new DenhaagButtonAnimation( ".gform_previous_button" );
    new DenhaagButtonAnimation( ".gform_next_button" );
  }
);
